import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/app/StatusBadge";
import CrudDialog, { ConfirmDeleteDialog } from "@/components/app/CrudDialog";
import RecordConversation from "@/components/app/RecordConversation";
import { toast } from "sonner";
import { Search, PlusCircle, Pencil, Trash2, Upload } from "lucide-react";
import ImportEmployeesDialog from "@/components/employees/ImportEmployeesDialog";

export default function Employees() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [masters, setMasters] = useState([]);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [importOpen, setImportOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/employees", { params: { q: q || undefined, limit: 50 } })
      .then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); })
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/master-companies")]).then(([c, m]) => {
      setCompanies(c.data.items || []);
      setMasters(m.data.items || []);
    });
  }, []);

  const archive = async (emp) => {
    try { await api.delete(`/employees/${emp.id}`); toast.success("Employee deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const isCreate = !dialog.initial;
  const fields = [
    { name: "name", label: "Name", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "employee_code", label: "Employee code", required: isCreate },
    { name: "phone", label: "Phone" },
    { name: "department", label: "Department" },
    { name: "cost_centre", label: "Cost centre" },
    { name: "country", label: "Country" },
    { name: "delivery_address", label: "Delivery address", type: "textarea" },
    ...(isCreate ? [
      { name: "master_company_id", label: "Master company", type: "select", required: true,
        options: masters.map((m) => ({ value: m.id, label: m.name })),
        onSelect: (_v, _form, set) => set("company_id", "") },
      { name: "company_id", label: "Subsidiary", type: "select", required: true,
        options: (form) => companies
          .filter((c) => c.master_company_id === form.master_company_id)
          .map((c) => ({ value: c.id, label: c.name })),
        // Hide (and skip the required check) when the chosen master company has no
        // subsidiaries — e.g. REMOTE — so the employee can be saved directly.
        hidden: (form) => !!form.master_company_id
          && companies.filter((c) => c.master_company_id === form.master_company_id).length === 0 },
    ] : []),
  ];

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Customers</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Employees</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total.toLocaleString()} employees across all companies you can access.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RecordConversation contextType="general" contextLabel="Employees" />
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="emp-import-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Import Excel
          </Button>
          <Button className="bg-slate-900" onClick={() => setDialog({ open: true, initial: null })} data-testid="emp-add-btn">
            <PlusCircle className="h-4 w-4 mr-1.5" /> Add employee
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search name, email, code…" className="pl-9 h-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="emp-search" />
        </div>
      </div>

      {/* Table — responsive Priority Engine:
            P1: Name · Status · Actions
            P2 (hidden < md): Email
            P3 (hidden < lg): Code · Department · Country
       */}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase text-slate-500">
              <th className="text-left px-3 py-2.5 hidden lg:table-cell" data-priority="p3">Code</th>
              <th className="text-left px-3 py-2.5" data-priority="p1">Name</th>
              <th className="text-left px-3 py-2.5 hidden md:table-cell" data-priority="p2">Email</th>
              <th className="text-left px-3 py-2.5 hidden lg:table-cell" data-priority="p3">Department</th>
              <th className="text-left px-3 py-2.5 hidden lg:table-cell" data-priority="p3">Country</th>
              <th className="text-left px-3 py-2.5" data-priority="p1">Status</th>
              <th className="text-right px-3 py-2.5" data-priority="p1">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">No employees found.</td></tr>
            ) : rows.map((e) => (
              <tr key={e.id} data-testid={`emp-row-${e.id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs hidden lg:table-cell">{e.employee_code}</td>
                <td className="px-3 py-2">
                  <div>{e.name}</div>
                  <div className="md:hidden text-[11px] text-slate-500 mt-0.5">{e.email}</div>
                  <div className="lg:hidden text-[11px] text-slate-500 font-mono">{e.employee_code}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 hidden md:table-cell">{e.email}</td>
                <td className="px-3 py-2 hidden lg:table-cell">{e.department}</td>
                <td className="px-3 py-2 hidden lg:table-cell">{e.country}</td>
                <td className="px-3 py-2"><StatusBadge value={e.active ? "active" : "inactive"} tone={e.active ? "green" : "slate"} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDialog({ open: true, initial: e })} data-testid={`emp-edit-${e.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => setConfirm(e)} data-testid={`emp-archive-${e.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CrudDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        title={dialog.initial ? `Edit ${dialog.initial.name}` : "Add employee"}
        fields={fields}
        initial={dialog.initial}
        endpoint={dialog.initial ? `/employees/${dialog.initial.id}` : "/employees"}
        method={dialog.initial ? "PATCH" : "POST"}
        onSaved={load}
        testIdPrefix="emp"
      />
      <ConfirmDeleteDialog
        open={!!confirm}
        onOpenChange={() => setConfirm(null)}
        title={`Delete ${confirm?.name}?`}
        description="The employee will be marked inactive. Blocked if they still hold assets."
        onConfirm={() => archive(confirm)}
        testId="emp-confirm"
      />
      <ImportEmployeesDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        masters={masters}
        companies={companies}
        onImported={load}
      />
    </div>
  );
}
